import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/** The values collected by the New Project drawer. */
export interface NewProjectFormValue {
  orgId: string;
  orgName: string;
  /** Atlas Project ID — maps to the API's `projectId`. */
  atlasId: string;
  projectName: string;
}

/**
 * Slide-out drawer for creating a new project (feedback round). Collects the
 * design's four required fields, validates them on submit, and emits the values
 * via `create`; the page persists them. Presentational — it owns only the form.
 */
@Component({
  selector: 'app-new-project-drawer',
  imports: [MatIconModule],
  templateUrl: './new-project-drawer.html',
  styleUrl: './new-project-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close.emit()',
  },
})
export class NewProjectDrawer {
  /** Disables the Create button while a create request is in flight. */
  readonly busy = input(false);

  /** Emitted when the drawer should close (backdrop, Cancel, X, or Escape). */
  readonly close = output<void>();

  /** Emitted with the validated form values when Create is clicked. */
  readonly create = output<NewProjectFormValue>();

  protected readonly orgId = signal('');
  protected readonly orgName = signal('');
  protected readonly atlasId = signal('');
  protected readonly projectName = signal('');
  protected readonly touched = signal(false);

  protected readonly valid = computed(
    () =>
      this.orgId().trim() !== '' &&
      this.orgName().trim() !== '' &&
      this.atlasId().trim() !== '' &&
      this.projectName().trim() !== '',
  );

  /** Whether a given field should show its required error (after a submit attempt). */
  protected showError(value: string): boolean {
    return this.touched() && value.trim() === '';
  }

  protected submit(): void {
    this.touched.set(true);
    if (!this.valid()) {
      return;
    }
    this.create.emit({
      orgId: this.orgId().trim(),
      orgName: this.orgName().trim(),
      atlasId: this.atlasId().trim(),
      projectName: this.projectName().trim(),
    });
  }
}
